/** @jsxImportSource @emotion/react */
import { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import {
    Line,
    LineChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
} from "recharts";
import { Text } from "@phoenix/components";
import {
    ChartTooltip,
    ChartTooltipItem,
    defaultCartesianGridProps,
    defaultLegendProps,
    defaultXAxisProps,
    defaultYAxisProps,
    useBinInterval,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { formatFloat } from "@phoenix/utils/numberFormatUtils";
import type { ToolLatencySummaryQuery } from "./__generated__/ToolLatencySummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
`;

const chartContainerCSS = css`
  flex: 1;
  min-height: 250px;
`;

const COLOR_PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#06b6d4"];

function CustomTooltip({
    active,
    payload,
    label,
    scale,
}: TooltipContentProps<number, string> & { scale?: string }) {
    if (active && payload && payload.length) {
        const date = new Date(label || "");
        const isDayScale = scale === "DAY";
        const timeStr = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(isDayScale ? {} : { hour: '2-digit', minute: '2-digit', hour12: true })
        });

        return (
            <ChartTooltip>
                <div style={{ marginBottom: 4, fontWeight: 700, fontSize: 12 }}>{timeStr}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {payload.map((p, i) => (
                        <ChartTooltipItem
                            key={i}
                            color={p.color || "#6366f1"}
                            shape="circle"
                            name={p.name || ""}
                            value={`${formatFloat(p.value as number)}ms`}
                        />
                    ))}
                </div>
            </ChartTooltip>
        );
    }
    return null;
}

export function ToolLatencySummary({ projectId, timeRange, fetchKey, widgetId, widgetTitle, onDataExport }: ProjectMetricViewProps) {
    const defaultScale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    const scale = useMemo(() => {
        if (defaultScale === "MONTH" || defaultScale === "WEEK" || defaultScale === "DAY") return "DAY";
        return defaultScale;
    }, [defaultScale]);

    const data = useLazyLoadQuery<any>(
        graphql`
      query ToolLatencySummaryQuery($projectId: ID!, $timeRange: TimeRange!, $filterCondition: String!, $timeBinConfig: TimeBinConfig!) {
        project: node(id: $projectId) {
          ... on Project {
            spanCountTimeSeries(timeRange: $timeRange, timeBinConfig: $timeBinConfig, filterCondition: $filterCondition) {
              data {
                timestamp
              }
            }
            spans(timeRange: $timeRange, filterCondition: $filterCondition, first: 10000) {
              edges {
                node {
                  name
                  startTime
                  endTime
                  latencyMs
                }
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: { start: timeRange.start?.toISOString(), end: timeRange.end?.toISOString() },
            filterCondition: 'span_kind == "TOOL"',
            timeBinConfig: { scale, utcOffsetMinutes },
        },
        { fetchPolicy: "network-only", fetchKey }
    );

    const getPreciseMs = (iso: string) => {
        if (!iso) return 0;
        const date = new Date(iso);
        const match = iso.match(/\.(\d+)/);
        if (match) {
            const seconds = Math.floor(date.getTime() / 1000);
            const msFrac = parseFloat("0." + match[1]) * 1000;
            return seconds * 1000 + msFrac;
        }
        return date.getTime();
    };

    const { chartData, toolNames } = useMemo(() => {
        const spans = data.project?.spans?.edges ?? [];
        const timeSeries = data.project?.spanCountTimeSeries?.data ?? [];
        const names = new Set<string>();

        spans.forEach((e: any) => {
            if (e.node?.name) names.add(e.node.name);
        });
        const sortedNames = Array.from(names).sort();

        const timeBuckets: Record<string, Record<string, number[]>> = {};

        // Pre-fill buckets using the time series so the X-axis stretches correctly
        timeSeries.forEach((d: any) => {
            timeBuckets[d.timestamp] = {};
        });

        spans.forEach((e: any) => {
            const node = e.node;
            if (!node?.name || !node?.startTime) return;

            const date = new Date(node.startTime);
            let binTime: number;

            if (scale === "DAY") {
                binTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
            } else if (scale === "HOUR") {
                binTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours()).getTime();
            } else {
                binTime = date.getTime();
            }

            let closestTimestamp = "";
            let minDiff = Infinity;

            timeSeries.forEach((d: any) => {
                const ts = new Date(d.timestamp).getTime();
                const diff = Math.abs(ts - binTime);
                if (diff < minDiff) {
                    minDiff = diff;
                    closestTimestamp = d.timestamp;
                }
            });

            const gracePeriod = scale === "DAY" ? 43200000 : 1800000;
            const targetTime = (closestTimestamp && minDiff < gracePeriod) ? closestTimestamp : new Date(binTime).toISOString();

            if (!timeBuckets[targetTime]) timeBuckets[targetTime] = {};
            if (!timeBuckets[targetTime][node.name]) timeBuckets[targetTime][node.name] = [];

            const lat = (node.endTime && node.startTime)
                ? (getPreciseMs(node.endTime) - getPreciseMs(node.startTime))
                : (node.latencyMs ?? 0);

            timeBuckets[targetTime][node.name].push(lat);
        });

        // Use pre-filled timeseries array for ordering to prevent chart collapse
        const chart = Object.keys(timeBuckets).sort().map(timestamp => {
            const point: any = { timestamp };
            let hasAnyValue = false;

            sortedNames.forEach(name => {
                const values = timeBuckets[timestamp][name] || [];
                if (values.length > 0) {
                    point[name] = values.reduce((a, b) => a + b, 0) / values.length;
                    hasAnyValue = true;
                } else {
                    point[name] = null;
                }
            });

            // Required for Auto-Cropping if we want to add it later, or just tracking
            point._activity = hasAnyValue ? 1 : 0;
            return point;
        });

        // Smart cropping so it doesn't show completely empty graphs
        const firstActiveIdx = chart.findIndex(d => d._activity > 0);
        const lastActiveIdx = chart.length - [...chart].reverse().findIndex(d => d._activity > 0);

        const cropped = (firstActiveIdx === -1) ? chart : chart.slice(
            Math.max(0, firstActiveIdx - 2),
            Math.min(chart.length, lastActiveIdx + 2)
        );

        return { chartData: cropped, toolNames: sortedNames };
    }, [data, scale]);

    useEffect(() => {
        if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
            onDataExport(widgetId, widgetTitle, chartData);
        }
    }, [chartData, onDataExport, widgetId, widgetTitle]);

    const interval = useBinInterval({ scale });

    return (
        <div css={containerCSS}>
            <div css={chartContainerCSS}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                        <CartesianGrid {...defaultCartesianGridProps} vertical={false} strokeDasharray="3 3" opacity={0.3} />
                        <XAxis
                            {...defaultXAxisProps}
                            dataKey="timestamp"
                            tickFormatter={(x) => {
                                const d = new Date(x);
                                if (scale === "DAY") {
                                    return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric' });
                                }
                                return d.toLocaleString("en-US", { month: "numeric", day: "numeric", hour: "2-digit" });
                            }}
                            tick={{ fontSize: 10, fill: "var(--ac-global-text-color-500)" }}
                            interval={interval}
                        />
                        <YAxis {...defaultYAxisProps} width={40} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}ms`} />
                        <Tooltip content={(props: any) => <CustomTooltip {...props} scale={scale} />} />
                        {toolNames.map((name, i) => (
                            <Line
                                key={name}
                                type="monotone"
                                dataKey={name}
                                stroke={COLOR_PALETTE[i % COLOR_PALETTE.length]}
                                strokeWidth={2.5}
                                dot={{ fill: "#eab308", stroke: "#fff", strokeWidth: 1.5, r: 3 }}
                                activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                                connectNulls
                            />
                        ))}
                        <Legend {...defaultLegendProps} iconType="circle" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
